import { useCallback, useId, useMemo, useState, type ReactNode } from 'react';
import { formatDateTime } from '../utils/dateFormat';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query';
import { useDsServices, useDsTranslate, useDsNotify } from '../context/DsProvider';
import {
  activityActionLabel,
  activityKindLabel,
  annotationIdFromMessage,
  buildActivityEntries,
  commentIdFromMessage,
  hasReplySentinel,
  isContentEntry,
  isWordingReplacement,
  mergeWorkflowEvents,
  sentinelClause,
  sentinelLabel,
  sentinelPayload,
  reviewWaitingLabel,
  reviewWaitingOn,
  type ActivityEntry,
  type ActivityKind,
} from './reviewActivity';
import type {
  DocumentReview,
  ReviewAnnotation,
  ReviewTargetType,
} from '../types/review';
import type { AuditEvent } from '../types/signing';
import styles from './ReviewActivityPanel.module.scss';

interface ReviewActivityPanelProps {
  targetType: ReviewTargetType;
  targetId: string;
  reviews: DocumentReview[];
  /** Which side the person looking at this is on. Decides what "needs your action" means. */
  /** How a note is posted. Mirrors `ChatSidebar`'s three modes. */
  mode: 'author' | 'reviewer' | 'reviewer-internal';
  /** Reviewer token — `mode: 'reviewer'`. */
  token?: string;
  /** Review id — `mode: 'reviewer-internal'`. */
  reviewId?: string;
  /**
   * Does this caller have a note endpoint they are entitled to at all? Default `true`.
   *
   * Neither note route is universal: the author one wants `MASTER_TEMPLATES_SUBMIT` /
   * `CLIENT_MASTER_TEMPLATES_SUBMIT`, and `reviewer-note` refuses a reviewer who has already
   * finished on an envelope (`requireNotFinishedOnEnvelope`). A caller can therefore hold
   * neither — a client admin who has submitted their changes is both — and the composer would
   * offer a Send that can only 403 with the generic failure toast. Pass `false` there: the
   * portal's rule is hide, don't disable.
   */
  allowCompose?: boolean;
  /** Owns cache invalidation after a note is posted. Without it the panel invalidates its own. */
  onNoteSent?: () => void;
  /**
   * The activities query is still in flight.
   *
   * Without it the panel paints "Nothing has happened on this review yet." over a twelve-entry
   * history while the fetch is running, then pops the list in — the project's loading rule
   * forbids both halves of that.
   */
  loading?: boolean;
  /**
   * `rail` (default) is the docked column beside a document — it owns a left border, because
   * there is a document on the other side of it. `embedded` is the panel filling a surface that
   * frames and titles it already, such as an Activity tab: no border, and the heading stays as
   * the `aria-labelledby` target without being painted under a tab that already says Activity.
   */
  variant?: 'rail' | 'embedded';
  /**
   * The envelope's signing-audit events, merged into All activity as the prototype's
   * `workflow` kind.
   *
   * The prototype is explicit that this rail is "one history, not two lists: every document
   * change, comment, response, review action, workflow event and approval goes into it"
   * (`ReviewerDocumentPage.jsx:17-19`). Ours held only `review_activity`, whose `review_id`
   * is NOT NULL — so a request that was sent, opened and signed showed none of that, and a
   * request never sent for review had a permanently empty tab.
   *
   * Optional because only the AUTHOR surface can read them: `GET /v1/envelopes/{id}/audit` is
   * session-authed and envelope-scoped, so a snapshot reviewer has no envelope and an
   * external token holder has no session. Those surfaces render the review's half, which is
   * all there is for them — the panel's shape is identical either way.
   */
  workflowEvents?: AuditEvent[];
}

/**
 * When it happened, as a stamp rather than a countdown — the prototype's
 * `when()` (`ReviewerDocumentPage.jsx:123`): `26 Oct, 17:42`.
 *
 * It used to read "2m ago" / "3h ago". Two problems, and the second is the real one: nothing
 * re-ticks the label, so a screen left open — which a review screen is, for hours — kept
 * saying "2m ago" about something that happened at breakfast. A stamp is also what a reader
 * needs here: entries from several people over several days are being put in order against
 * each other, not against now.
 *
 * Year included only when it is not the current one, so an ordinary rail stays short while a
 * cycle from last year cannot read as this one.
 */
function formatWhen(iso: string): string {
  const then = new Date(iso);
  const ms = then.getTime();
  if (Number.isNaN(ms)) return '';
  const sameYear = then.getFullYear() === new Date().getFullYear();
  return then.toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    ...(sameYear ? {} : { year: 'numeric' }),
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Pill and dot class per kind — the prototype's `TYPE_META` (`ReviewerDocumentPage.jsx:41-48`)
 * resolved onto the `--ds-*` palette. `Record` rather than a ternary chain so adding a kind to
 * {@link ActivityKind} fails the build here instead of silently rendering the neutral pill.
 */
const KIND_PILL: Record<ActivityKind, string> = {
  change: styles.kindChange!,
  comment: styles.kindComment!,
  response: styles.kindResponse!,
  approval: styles.kindApproval!,
  review: styles.kindReview!,
  workflow: styles.kindWorkflow!,
};

const KIND_DOT: Record<ActivityKind, string> = {
  change: styles.dotChange!,
  comment: styles.dotComment!,
  response: styles.dotResponse!,
  approval: styles.dotApproval!,
  review: styles.dotReview!,
  workflow: styles.dotWorkflow!,
};

/**
 * The review's history, read as a history: what happened, who did it, and what is still
 * waiting on you.
 *
 * Replaces the floating `ChatWidget` on the reviewer surfaces. The bubble printed every entry
 * the same way — a tracked change, a comment and "review started" were three identical lines —
 * carried a badge computed from a `localStorage` read marker rather than from anything about
 * the review, and overlapped the document it was describing. This takes the column the layout
 * already leaves, splits the record into what needs you and what merely happened, and says at
 * the top who the review is sitting with.
 *
 * The quoted passage and threaded replies landed with BE V33, and DOCX reviewing is recorded
 * too: `ReviewDocxActivityRecorder` reads `word/comments.xml` and the tracked changes out of the
 * file on every OnlyOffice save and writes `DOCUMENT_COMMENT` / `DOCUMENT_SUGGESTION` rows. That
 * runs off the editor's save webhook, which is ONE path — so it covers the internal reviewer
 * and the external token holder alike, and neither needs anything from this component. A DOCX
 * comment therefore carries its anchored passage; the rolled-up suggestion entry carries a count
 * instead, as a sentinel this panel translates.
 *
 * <p><b>An author-side history too, not only a live rail.</b> `reviews` may hold every round ever
 * run on the target — including none, because this is mounted on surfaces where no review
 * exists yet. It groups by cycle, says which nothing it is looking at, and drops the composer
 * when there is no live review to post into rather than offering a Send that can only fail.
 */
export function ReviewActivityPanel({
  targetType,
  targetId,
  reviews,
  mode,
  token,
  reviewId,
  allowCompose = true,
  onNoteSent,
  loading = false,
  variant = 'rail',
  workflowEvents,
}: ReviewActivityPanelProps) {
  const t = useDsTranslate();
  const queryClient = useQueryClient();
  const notify = useDsNotify();
  const { review: reviewService, reviewPublic: reviewPublicService } = useDsServices();
  const [noteText, setNoteText] = useState('');
  const titleId = useId();

  const reviewEntries = useMemo(() => buildActivityEntries(reviews), [reviews]);
  /**
   * The review's own history and the envelope's workflow, in one list, newest first.
   *
   * A workflow event DOES reach this rail — it renders as a quiet node rather than a card,
   * because `mergeWorkflowEvents` gives it a `headline` and `isContentEntry` reads that as
   * "something happened" rather than "somebody wrote this". The noise reduction is
   * `HIDDEN_AUDIT_ACTIONS`, applied inside the merge, not a filter here.
   *
   * (An earlier revision of this comment claimed the opposite, and invited deleting the merge.
   * Acting on it would have silently removed sent / signed / completed from every signing
   * request's Activity tab, with no type error to catch it.)
   */
  const entries = useMemo(
    () => mergeWorkflowEvents(reviewEntries, workflowEvents, t),
    [reviewEntries, workflowEvents, t],
  );
  const multiCycle = reviews.length > 1;
  /**
   * Replies are nested under their parent, so a plain filter over `entries` would miss one that
   * needs an answer, and `entries.length` would under-report the history. Both read the flat
   * set instead.
   */
  const flat = useMemo(() => entries.flatMap((e) => [e, ...e.replies]), [entries]);

  /* ── Putting the words back into a comment ──────────────────────────────────
   *
   * An annotation's activity row stores a SENTINEL, not what the person typed
   * (`ReviewAnnotationServiceImpl`: the row gets `annotation_created:<id>` and the highlighted
   * passage; the comment itself is saved on `review_annotation.message` and never copied
   * across). Suppressing the sentinel — which is right, nobody should read a uuid — left the
   * entry with a verb and a quotation and no remark, so the feed said THAT someone commented
   * and never WHAT they said.
   *
   * So fetch the rows the sentinels name and put the text back. No backend change: both
   * endpoints already exist and both clients are already written. The server could carry the
   * message on the activity instead and save these reads — raised as a backend ask — but the
   * join is correct meanwhile and needs nothing from anyone.
   */
  const annotationIds = useMemo(() => {
    const ids = new Set<string>();
    for (const e of flat) {
      const id = annotationIdFromMessage(e.activity.message);
      if (id) ids.add(id);
    }
    return [...ids].sort();
  }, [flat]);

  /** Only a history that actually contains a reply pays for the per-annotation comment reads. */
  const wantsReplies = useMemo(() => hasReplySentinel(flat.map((e) => e.activity.message)), [flat]);

  const byToken = mode === 'reviewer' && !!token;
  const annotationSources = useMemo(
    () => (byToken ? [token as string] : reviews.map((r) => r.id)),
    [byToken, token, reviews],
  );

  /**
   * `combine` rather than reading the array of results: `useQueries` returns a fresh array on
   * every render, so a `useMemo` over it would rebuild the map every time and hand every entry
   * a new object. `combine` recomputes only when a result actually changes.
   */
  /**
   * The read, resolved once. Both services are optional on the provider, and narrowing them
   * here rather than at each call site is what keeps the query definitions below readable —
   * no service, no queries, and the entries fall back to what they show today.
   */
  const readAnnotations = useMemo(() => {
    if (byToken) {
      return reviewPublicService
        ? (source: string) => reviewPublicService.listAnnotationsByToken(source)
        : null;
    }
    return reviewService ? (source: string) => reviewService.listAnnotations(source) : null;
  }, [byToken, reviewPublicService, reviewService]);

  const readComments = useMemo(() => {
    if (byToken) {
      return reviewPublicService
        ? (source: string, annotationId: string) =>
            reviewPublicService.listAnnotationCommentsByToken(source, annotationId)
        : null;
    }
    return reviewService
      ? (source: string, annotationId: string) =>
          reviewService.listAnnotationComments(source, annotationId)
      : null;
  }, [byToken, reviewPublicService, reviewService]);

  const annotationQueries = useMemo(() => {
    if (!readAnnotations || annotationIds.length === 0) return [];
    return annotationSources.map((source) => ({
      queryKey: ['ds-review-annotations', byToken ? 'token' : 'review', source] as const,
      queryFn: () => readAnnotations(source),
      // The panel is already refetched by SSE and by its parent's polling, and the text of a
      // comment that has been made does not change on its own.
      staleTime: 60_000,
    }));
  }, [readAnnotations, annotationIds.length, annotationSources, byToken]);

  const commentQueries = useMemo(() => {
    if (!readComments || !wantsReplies) return [];
    return annotationIds.flatMap((annotationId) =>
      annotationSources.map((source) => ({
        queryKey: [
          'ds-review-annotation-comments',
          byToken ? 'token' : 'review',
          source,
          annotationId,
        ] as const,
        queryFn: () => readComments(source, annotationId),
        staleTime: 60_000,
      })),
    );
  }, [readComments, wantsReplies, annotationIds, annotationSources, byToken]);

  /**
   * `combine` rather than reading the results array: `useQueries` returns a fresh array every
   * render, so a `useMemo` over it would rebuild the map each time and hand every entry a new
   * object. `combine` recomputes only when a result actually changes.
   */
  /**
   * The annotation ROW, not just its message: the page it sits on is the other half of what a
   * reviewer wants at a glance, and it lives on the same row the remark does — so carrying the
   * whole thing costs nothing and saves a second lookup.
   */
  const annotationById = useQueries({
    queries: annotationQueries,
    combine: (results) => {
      const map = new Map<string, ReviewAnnotation>();
      for (const r of results) {
        for (const a of r.data ?? []) map.set(a.id, a);
      }
      return map;
    },
  });

  const commentById = useQueries({
    queries: commentQueries,
    combine: (results) => {
      const map = new Map<string, string>();
      for (const r of results) {
        for (const c of r.data ?? []) map.set(c.id, c.message);
      }
      return map;
    },
  });

  /**
   * What a sentinel entry actually said, or undefined when the row it names holds nothing
   * (an empty highlight with no remark) or has not been read yet. Never the sentinel itself —
   * that is the leak this whole mechanism exists to keep off the screen.
   */
  const resolveSentinelNote = useCallback(
    (message: string | undefined): string | undefined => {
      const annotationId = annotationIdFromMessage(message);
      if (annotationId) return annotationById.get(annotationId)?.message?.trim() || undefined;
      const commentId = commentIdFromMessage(message);
      if (commentId) return commentById.get(commentId)?.trim() || undefined;
      return undefined;
    },
    [annotationById, commentById],
  );
  /**
   * Document name by `review_document` id, for naming WHICH document an entry is about — and
   * separately, how many CONTRACT documents there actually are.
   *
   * The two are different counts and conflating them was a bug: a `review_document` row is a
   * per-ROUND copy (hence `envelopeDocumentId` beside its own `id`), and this panel is handed
   * every round ever run on the target. So counting ids said "several documents" about a
   * single-document request that had been round twice, and every chip then carried a document
   * name nobody needed.
   */
  const { documentNameById, distinctDocuments } = useMemo(() => {
    const byId = new Map<string, string>();
    const distinct = new Set<string>();
    for (const review of reviews) {
      for (const doc of review.documents ?? []) {
        if (!doc.documentName) continue;
        byId.set(doc.id, doc.documentName);
        distinct.add(doc.envelopeDocumentId ?? doc.documentName);
      }
    }
    return { documentNameById: byId, distinctDocuments: distinct.size };
  }, [reviews]);
  /** One document needs no naming — every entry is about it. */
  const multiDocument = distinctDocuments > 1;

  /**
   * WHERE in the contract this entry is — the third thing a reviewer wants at a glance, after
   * what changed and who changed it (demo feedback, 2026-10-29).
   *
   * Two questions, in that order: which DOCUMENT, then where inside it.
   *
   * **Which document** only matters when there is more than one, and one review covers them
   * all. Until V36 only an annotation row could answer it — a tracked change or a Word
   * comment carried nothing — so on a two-document signing request the half of the trail
   * that comes from OnlyOffice read as though it were about whichever contract happened to
   * be on screen (reported from staging, 2026-11-03).
   *
   * **Where inside it** is answered in the terms that document has:
   *
   * - A **PDF annotation** gives a page: the row carries `pageNumber`.
   * - A **DOCX edit** has no page — Word's XML holds no pagination, which is a layout the
   *   renderer decides — so the backend names the clause the edit sits in and the sentinel
   *   carries it. It names a heading or nothing; it never guesses, because a wrong clause sends
   *   someone to the wrong part of a contract, which is worse than saying nothing.
   *
   * A row can answer one, both, or neither. A workflow milestone is about no passage and
   * usually no single document, and gets no pill at all.
   */
  const locationOf = useCallback(
    (activity: ActivityEntry['activity']): string | undefined => {
      const message = activity.message;
      const annotationId = annotationIdFromMessage(message);
      const annotation = annotationId ? annotationById.get(annotationId) : undefined;

      /**
       * WHICH document, before where inside it.
       *
       * The row's own `reviewDocumentId` first — the DOCX recorder stamps it (V36) and it is
       * the only thing that can answer this for a tracked change or a Word comment, which
       * carry no annotation to look through. An annotation row falls back to the annotation,
       * which has carried the document since long before the column existed, so a review
       * recorded under the old schema still names its document here.
       */
      const documentId = activity.reviewDocumentId ?? annotation?.reviewDocumentId;
      const docName = documentId ? documentNameById.get(documentId) : undefined;
      const parts: string[] = [];
      if (multiDocument && docName) parts.push(docName);

      // Then where inside it, in the terms that document has: a page for a PDF annotation,
      // the enclosing clause for a DOCX edit. Never both — no row is ever both kinds.
      const clause = sentinelClause(message);
      if (clause) {
        parts.push(clause);
      } else if (annotation && Number.isFinite(annotation.pageNumber) && annotation.pageNumber > 0) {
        parts.push(t('K_REVIEW_ON_PAGE', 'Page {n}').replace('{n}', String(annotation.pageNumber)));
      }
      return parts.length > 0 ? parts.join(' \u00b7 ') : undefined;
    },
    [annotationById, documentNameById, multiDocument, t],
  );

  const waiting = useMemo(() => reviewWaitingOn(reviews), [reviews]);
  const waitingText = reviewWaitingLabel(waiting, t);

  const activeReview = useMemo(
    () =>
      reviews.find((r) => r.status === 'IN_PROGRESS' || r.status === 'PENDING') ??
      reviews[0] ??
      null,
    [reviews],
  );

  /**
   * Is there anything for a note to land IN?
   *
   * Having NO review is the only state the author composer cannot work in — that is the case
   * where the mutation threw `No active review or service` and the panel reported it as the
   * generic "Failed to send message." A SETTLED round is deliberately still postable:
   * `ReviewServiceImpl.addNote` carries no status guard (its section is headed "Notes (work
   * regardless of review status)") and still fans `*_REVIEW_RESPONDED` out to the reviewers, so
   * this is the author's reply channel after a round closes. Gating on IN_PROGRESS/PENDING — as
   * an earlier pass here did — removed that channel outright on any surface showing one
   * completed round, with no composer and nothing saying why.
   *
   * The reviewer modes are untouched: holding a reviewer session IS a live review, and they post
   * by token / reviewId without consulting `activeReview`.
   */
  const canPostNote = allowCompose && (mode !== 'author' || !!activeReview);

  const addNoteMutation = useMutation({
    mutationFn: async (message: string) => {
      if (mode === 'author') {
        if (!activeReview || !reviewService) throw new Error('No active review or service');
        return reviewService.addNote(activeReview.id, message);
      }
      if (mode === 'reviewer-internal') {
        if (!reviewId || !reviewService) throw new Error('No reviewId or review service');
        return reviewService.addReviewerNote(reviewId, message);
      }
      if (!token || !reviewPublicService) throw new Error('No token or reviewPublic service');
      return reviewPublicService.addReviewerNote(token, message);
    },
    onSuccess: () => {
      // Caller-owned invalidation, same contract as ChatSidebar: a consumer with its own query
      // keys refreshes them itself rather than having this component guess at them.
      if (onNoteSent) {
        onNoteSent();
      } else {
        void queryClient.invalidateQueries({ queryKey: ['ds-reviews', targetType, targetId] });
        if (token) {
          void queryClient.invalidateQueries({ queryKey: ['ds-review-history', token] });
          void queryClient.invalidateQueries({ queryKey: ['ds-review-activities', token] });
        }
      }
      setNoteText('');
    },
    onError: () => {
      notify({ variant: 'critical', title: t('K_REVIEW_NOTE_ERROR', 'Failed to send message.') });
    },
  });

  const handleSend = useCallback(() => {
    const trimmed = noteText.trim();
    if (!trimmed) return;
    addNoteMutation.mutate(trimmed);
  }, [noteText, addNoteMutation]);

  /**
   * Walks the list once, emitting a label when the review cycle changes.
   *
   * `shape` picks the form the prototype gives each section: All activity is a TIMELINE
   * (dot gutter plus a connector), Needs your action is a stack of amber CARDS. Same entries,
   * two presentations — the second is a to-do list, and a to-do list threaded onto a timeline
   * reads as history rather than as something waiting for you.
   */
  const renderList = (
    list: ActivityEntry[],
    withReplies = true,
    shape: 'timeline' | 'card' = 'timeline',
  ) => {
    const out: ReactNode[] = [];
    let seen: number | null = null;
    for (const entry of list) {
      if (multiCycle && entry.cycle > 0 && entry.cycle !== seen) {
        seen = entry.cycle;
        out.push(
          // Keyed on the position too, not the review alone: `top` is sorted globally across
          // reviews, so a late entry on an earlier cycle makes the same header appear twice.
          <div key={`cycle-${entry.reviewId}-${out.length}`} className={styles.cycle}>
            {t('K_REVIEW_CYCLE', 'Review cycle {n}').replace('{n}', String(entry.cycle))}
          </div>,
        );
      }
      out.push(renderEntry(entry, withReplies, undefined, shape));
    }
    return out;
  };

  /**
   * `shape`, not `variant`: the component's own `variant` prop ('rail' | 'embedded') is a
   * different axis, and naming the parameter the same thing made that prop unreachable from
   * the two functions that do all the rendering.
   */
  const renderEntry = (
    entry: ActivityEntry,
    withReplies = true,
    parentQuote?: string,
    shape: 'timeline' | 'card' = 'timeline',
  ) => {
    const { activity, kind } = entry;
    /**
     * A workflow step, on the SAME thread as everything else.
     *
     * It was a full-width row with a hairline above it, which cut the dot trail into segments
     * and read as a divider between unrelated things rather than as a moment in one history.
     * Now it is an entry like any other — a small hollow node on the rail, one line of text —
     * so the column reads top to bottom as a single sequence and the cards stand out by
     * carrying more, not by being the only things attached to the line.
     *
     * A non-content row with replies under it still renders here rather than as a card: the
     * thread hangs off it, and dropping the parent would take the whole thread silently.
     */
    if (!isContentEntry(entry)) {
      return (
        <div key={activity.id} className={`${styles.entry} ${styles.entryQuiet}`}>
          <div className={styles.rail} aria-hidden="true">
            <span className={`${styles.kindDot} ${styles.dotQuiet}`} />
          </div>
          <div className={styles.quietBody}>
            <p className={styles.quietLine}>
              {entry.headline ??
                sentinelLabel(activity.message, t) ??
                activityActionLabel(activity.action, t)}
            </p>
            {/* WP-16178/16179 — this cell now ellipses, so its `title` has to carry the actor
                too. It used to hold the timestamp alone, which was enough while the cell could
                not shrink; now that it gives way to the label, the name is the first thing
                clipped and the tooltip is the only way back to it. */}
            <span
              className={styles.quietWhen}
              title={
                (activity.actorName ? `${activity.actorName} · ` : '') +
                formatDateTime(activity.createdOn)
              }
            >
              {activity.actorName ? `${activity.actorName} · ` : ''}
              {formatWhen(activity.createdOn)}
            </span>
          </div>
          {/* A THIRD GRID CELL pinned to column 2, not a wrapper around the whole row. Wrapping
              it put `.entry` behind an unclassed div, so `.entry:last-child` — the rule that
              hides the trailing connector — matched every quiet node and removed the rail
              from all of them, which is the exact continuity this tier exists for. */}
          {withReplies && entry.replies.length > 0 && (
            <div
              className={`${styles.replies} ${styles.quietReplies}`}
              role="group"
              aria-label={t('K_REVIEW_REPLIES', 'Replies')}
            >
              {entry.replies.map((reply) =>
                renderEntry(reply, false, activity.quotedText, 'card'),
              )}
            </div>
          )}
        </div>
      );
    }
    /**
     * A sentinel stand-in outranks the action it rides on. All five annotation paths record
     * `NOTE_ADDED`, so on four of them the action line reads "Added a note" for something that
     * was not an addition — and with the quote now directly above it, a deletion announced
     * itself as "Added a note" over the very clause it had NOT removed.
     */
    const sentinel = sentinelLabel(activity.message, t);
    // A merged signing-audit event carries its own, because its `action` is an `AuditAction`
    // and the review table knows nothing about it.
    const headline = entry.headline ?? sentinel ?? activityActionLabel(activity.action, t);
    /**
     * A sentinel row's words live on the annotation (or the reply) it names, so they are
     * looked up rather than read off the activity. A human-written note needs no lookup — it
     * IS the message. Falls back to nothing, never to the sentinel: a uuid on screen is the
     * defect this suppression was added for.
     */
    /**
     * A DOCX-edit sentinel carries its words itself, so it is read before anything is fetched:
     * the wording half of `docx_added:6. Remuneration\u001fpayable on the 25th` IS the new
     * wording, and the clause half goes to the `where` pill below. An annotation sentinel names
     * a row instead, and that one has to be looked up.
     */
    const note = sentinel
      ? (sentinelPayload(activity.message) ?? resolveSentinelNote(activity.message))
      : activity.message;
    const where = locationOf(activity);
    /**
     * A reply is about the SAME passage as the entry it answers — the server copies the
     * annotation's text onto the update / delete rows too. Printing it again, indented and
     * 12px below an identical block, is the duplication the threading was meant to end.
     * Only a CHANGED passage is news.
     */
    const quote = activity.quotedText !== parentQuote ? activity.quotedText : undefined;

    const body = (
      <>
        <div className={styles.entryTop}>
          <span className={`${styles.kind} ${KIND_PILL[kind]}`}>{activityKindLabel(kind, t)}</span>
          {/* The relative label is computed at render and nothing re-ticks it, so a session
              left open keeps saying "2m ago". The title carries the real stamp. */}
          <span className={styles.when} title={formatDateTime(activity.createdOn)}>
            {formatWhen(activity.createdOn)}
          </span>
          {/* Where in the contract. On the meta row rather than under the quote: it is the
              same kind of fact as the timestamp — something to scan past, not to read.
              `title` because this pill clamps: "Page 3" always fitted, but a clause label is
              document text up to 80 characters and the rail is ~380px, so without it the tail
              is dropped with no way to recover it. Same treatment the timestamp, the quote and
              both halves of the transition already get. */}
          {where && (
            <span className={styles.where} title={where}>
              {where}
            </span>
          )}
        </div>
        {/* Who, then what, as one sentence — the prototype's "<b>Joan Marwa</b> — asked for a
            change", not a name chip on the meta row above it. The name is the only part of the
            line set in the heading colour, which is what makes a column of entries scannable
            by person. */}
        <p className={styles.line}>
          {/* A system-recorded workflow event can have no actor at all, and the separator then
              opened the line with a dash and no subject. */}
          {activity.actorName ? (
            <>
              <b className={styles.actor}>{activity.actorName}</b>
              {headline ? ` — ${headline}` : ''}
            </>
          ) : (
            headline
          )}
        </p>
        {/* The passage and the remark, boxed together: they are one quotation and its comment,
            and the prototype draws them as a single bordered block so neither reads as part of
            the sentence above. Absent on most entries — a note typed here refers to nothing —
            so it renders only where there is something to put in it. */}
        {(quote || (note && note !== headline)) && (
          <div className={styles.detail}>
            {/* A rewrite is ONE fact, so it is drawn as one: the old wording struck through,
                an arrow, then what it now reads. Two stacked blocks — a quotation and a remark
                — make the reader work out that the second replaced the first, which is the
                whole thing they came to the rail to find out.

                Both halves clamp to two lines with `title` carrying the rest: a replaced
                clause can be a paragraph, and one entry must not bury the rail. */}
            {/* Both halves or neither: a `docx_replaced:` row whose payload went missing falls
                through to the plain quote below, which shows the OLD wording under the
                headline "Replaced wording" and nothing claiming to be the new. Degraded, but
                nothing on screen is false. */}
            {isWordingReplacement(activity.message) && quote && note ? (
              <p className={styles.transition}>
                <del className={styles.wasWording} title={quote}>
                  {quote}
                </del>
                {/* Decorative: the sentence is already readable as before-and-after from the
                    struck-through half, and a screen reader announces `del` / `ins` itself. */}
                <span className={styles.transitionArrow} aria-hidden="true">
                  →
                </span>
                <ins className={styles.nowWording} title={note}>
                  {note}
                </ins>
              </p>
            ) : (
              <>
                {quote && (
                  /* `title` carries the whole passage: the rail is ~380px, so the four-line
                     clamp shows roughly a fifth of the server's 1000-character cap. */
                  <blockquote className={styles.quote} title={quote}>
                    {quote}
                  </blockquote>
                )}
                {/* Only when a human wrote it — the action line already says what happened, so
                    repeating a stand-in for a machine message underneath would say it twice. */}
                {note && note !== headline && <p className={styles.note}>{note}</p>}
              </>
            )}
          </div>
        )}
        {withReplies && entry.replies.length > 0 && (
          /* Named group, not a bare div: indentation and a rule say "reply" to someone looking
             at it and nothing at all to someone listening to it, and which entry answers which
             is the whole point of the nesting. */
          <div
            className={styles.replies}
            role="group"
            aria-label={t('K_REVIEW_REPLIES', 'Replies')}
          >
            {/* An explicit arrow, not a bare `renderEntry` reference: `map` passes the index
                as the second argument, which would land on `withReplies`. A reply is never a
                timeline row — it is indented under its parent. */}
            {entry.replies.map((reply) => renderEntry(reply, false, activity.quotedText, 'card'))}
          </div>
        )}
      </>
    );

    if (shape === 'card') {
      return (
        <div key={activity.id} className={styles.card}>
          {body}
        </div>
      );
    }

    return (
      <div key={activity.id} className={styles.entry}>
        {/* The dot gutter. Decorative: the dot encodes the same kind the pill beside it already
            names in words, and the connector is a line. Its continuation is drawn by CSS off
            `:last-child`, so nothing here has to know the list's length — and a cycle heading,
            which is a sibling, can never BE last because it always precedes its entries. */}
        <div className={styles.rail} aria-hidden="true">
          <span className={`${styles.kindDot} ${KIND_DOT[kind]}`} />
        </div>
        <div className={styles.entryBody}>{body}</div>
      </div>
    );
  };

  return (
    <aside
      className={`${styles.panel} ${variant === 'embedded' ? styles.panelEmbedded : ''}`}
      aria-labelledby={titleId}
    >
      {/* In `embedded` mode the heading is clipped (the tab names the panel) and the waiting
          line is absent until a review exists, so the head has nothing in flow — keep the
          element for the heading's sake, drop the padding and the rule. */}
      <div
        className={`${styles.head} ${
          variant === 'embedded' && !waitingText ? styles.headEmpty : ''
        }`}
      >
        <h2
          id={titleId}
          className={`${styles.title} ${variant === 'embedded' ? styles.titleHidden : ''}`}
        >
          {t('K_REVIEW_ACTIVITY', 'Activity')}
        </h2>
        {waitingText && (
          <span className={styles.waiting}>
            {/* `author-decision` (WP-16183) keeps the default dot alongside `reviewers`: both
                are open states with someone still to act. It is deliberately NOT `dotWorkpay`
                — the side owing the decision is whoever authored the request, which is the
                client on a client-owned one, so that dot would name the wrong party. */}
            <span
              className={`${styles.dot} ${
                waiting === 'workpay'
                  ? styles.dotWorkpay
                  : waiting === 'complete'
                    ? styles.dotComplete
                    : ''
              }`}
              aria-hidden="true"
            />
            {waitingText}
          </span>
        )}
      </div>

      <div className={styles.scroll}>
        {/* There is deliberately NO "Needs your action" section (removed 2026-11-02, demo
            feedback: "remove needs your action, it is not adding any value — looking at that
            section I cannot get any useful info").

            It was a FILTERED COPY of the list below it, so every outstanding entry was printed
            twice on one screen and the rail read as twice as long as the review actually was.
            What it filtered on was derived, not known: there is no "needs action" flag on the
            wire, so it meant "the other side asked and nothing from your side has answered
            since" — a guess that could not see an answer given in the document itself, which
            on a DOCX review is where answers are given. The entries it promoted are still in
            the trail below, in order, saying the same thing once. */}
        <section className={styles.section}>
          <div className={styles.sectionHead}>
            <h3 className={styles.sectionTitle}>{t('K_REVIEW_ALL_ACTIVITY', 'All activity')}</h3>
            {/* "11 entries", not a bare "11" — a lone number beside a heading reads as a
                to-do count, which is what the amber pill above it actually is. */}
            <span className={styles.sectionCount}>
              {flat.length === 1
                ? t('K_REVIEW_ONE_ENTRY', '1 entry')
                : t('K_REVIEW_N_ENTRIES', '{n} entries').replace('{n}', String(flat.length))}
            </span>
          </div>
          {loading ? (
            <p className={styles.empty}>{t('K_REVIEW_ACTIVITY_LOADING', 'Loading activity…')}</p>
          ) : entries.length === 0 ? (
            <p className={styles.empty}>
              {/* Two different nothings. "Nothing has happened on this review yet" is a claim
                  about a review, and on a template never sent for one it describes a review the
                  reader does not have. */}
              {reviews.length === 0
                ? t('K_REVIEW_ACTIVITY_NO_REVIEWS', 'This has not been sent for review yet.')
                : t('K_REVIEW_ACTIVITY_EMPTY', 'Nothing has happened on this review yet.')}
            </p>
          ) : (
            <div className={styles.list}>{renderList(entries)}</div>
          )}
        </section>
      </div>

      {canPostNote && (
        <div className={styles.composer}>
          {/* The package's own primitives, not a hand-rolled pair. A local button here rendered
              white on the brand fill at 3.04:1 in ds-external and had no pending state beyond
              half opacity; `Button` carries both. */}
          <Input
            type="text"
            className={styles.input}
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleSend();
              }
            }}
            placeholder={t('K_PLACEHOLDER_REVIEW_NOTE', 'Type a message...')}
            aria-label={t('K_PLACEHOLDER_REVIEW_NOTE', 'Type a message...')}
          />
          <Button
            type="button"
            /* `md`, not `sm`: it sits beside a 14px field, and `sm` is 12px type. Height comes
               from the row's `stretch`, so size only picks the type and the side padding. */
            size="md"
            className={styles.send}
            onClick={handleSend}
            loading={addNoteMutation.isPending}
            disabled={!noteText.trim() || addNoteMutation.isPending}
          >
            {t('K_BUTTON_SEND', 'Send')}
          </Button>
        </div>
      )}
    </aside>
  );
}

export default ReviewActivityPanel;
